import React, { useEffect } from 'react';
import { Check, ArrowRight, AlertTriangle, Hammer, Ruler, Calculator, Instagram, BookOpen, ShieldCheck, Timer } from 'lucide-react';
import { initPixel, trackPageView } from './utils/metaPixel';
import { CountdownTimer } from './components/CountdownTimer';
import { PurchasePopup } from './components/PurchasePopup';
import { TestimonialCard } from './components/TestimonialCard';
export function App() {
  useEffect(() => {
    initPixel();
    trackPageView();
  }, []);
  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing');
    if (pricingSection) {
      pricingSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const testimonials = [{
    name: 'Martín Rodríguez',
    age: 34,
    city: 'Buenos Aires',
    quote: 'Antes tiraba precios al aire y siempre salía perdiendo. Con la calculadora del kit, ahora paso presupuestos en 5 minutos y sé exactamente cuánto gano. Me cambió el negocio.'
  }, {
    name: 'Laura Fernández',
    age: 29,
    city: 'Córdoba',
    quote: 'Empecé hace poco y me frustraba mucho trabarme a la mitad. Los planos paso a paso son una salvación, es como tener un profesor al lado diciéndote qué cortar primero.'
  }, {
    name: 'Carlos Gómez',
    age: 45,
    city: 'Rosario',
    quote: 'Llevo años en esto pero siempre fui desordenado. El método de trabajo me ordenó el taller y la cabeza. Ahora saco los trabajos en la mitad de tiempo.'
  }, {
    name: 'Sofía Martínez',
    age: 31,
    city: 'Mendoza',
    quote: 'Lo que más me sirvió fue la guía de redes. No sabía cómo mostrar mis muebles y ahora tengo consultas todas las semanas. El kit se pagó solo con la primera venta.'
  }, {
    name: 'Diego Sánchez',
    age: 38,
    city: 'La Plata',
    quote: 'La cantidad de madera que dejé de desperdiciar es increíble. Antes cortaba y si erraba, a la basura. Con los planos de corte optimizás todo el material.'
  }, {
    name: 'Ana López',
    age: 42,
    city: 'Tucumán',
    quote: 'Mis clientes notan la diferencia. Cuando les muestro el diseño y les paso un presupuesto detallado, confían más. Cerré tres cocinas este mes gracias a esa confianza.'
  }, {
    name: 'Roberto Torres',
    age: 51,
    city: 'Mar del Plata',
    quote: 'Pensé que a mi edad ya no iba a cambiar mi forma de trabajar, pero me equivoqué. Dejé de improvisar y ahora disfruto más el oficio, sin tanto estrés ni corridas.'
  }];
  return <div className="min-h-screen bg-stone-50 font-sans text-gray-900" data-id="element-0">
      <PurchasePopup data-id="element-1" />

      {/* Hero Section */}
      <header className="bg-gradient-to-b from-stone-900 to-stone-800 text-white pt-8 pb-16 px-4 text-center relative overflow-hidden" data-id="element-2">
        <div className="absolute inset-0 opacity-10 bg-[url('https://images.unsplash.com/photo-1622296089863-eb7fc530daa8?q=80&w=2070&auto=format&fit=crop')] bg-cover bg-center mix-blend-overlay" data-id="element-3"></div>
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-4">
          <h1 className="text-4xl md:text-6xl font-black mb-4 tracking-tight text-orange-500 drop-shadow-sm" data-id="element-5">
            KIT CARPINTERÍA PRO
          </h1>
          <p className="text-xl md:text-2xl font-medium text-stone-200 mb-8 max-w-2xl mx-auto leading-relaxed" data-id="element-6">
            Si improvisás, perdés. Si cobrás a ojo, regalás.
          </p>

          <div className="cursor-pointer transform hover:scale-[1.02] transition-transform duration-300 mx-auto max-w-[300px] md:max-w-[400px] shadow-2xl rounded-lg overflow-hidden mb-8" onClick={scrollToPricing} data-id="element-7">
            <img src="https://cdn.magicpatterns.com/uploads/xk6WvM2iMCARtCyBMENHfE/Kit_Carpintero_Pro_bundle_corregido_9x16.png" alt="Kit Carpintería Pro Bundle" className="w-full h-auto" data-id="element-8" />
          </div>

          <p className="text-lg md:text-xl text-stone-300 font-medium italic" data-id="element-9">
            "Dejá de dudar, dejá de desperdiciar y empezá a trabajar con
            método."
          </p>
        </div>
      </header>

      {/* Problem Section */}
      <section className="py-16 px-4 bg-white" data-id="element-10">
        <div className="max-w-3xl mx-auto" data-id="element-11">
          <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-10 rounded-r-lg" data-id="element-12">
            <h3 className="text-xl font-bold text-red-800 mb-2 flex items-center gap-2" data-id="element-13">
              <AlertTriangle className="w-6 h-6" data-id="element-14" />
              Te hago una pregunta simple:
            </h3>
            <p className="text-lg text-red-900" data-id="element-15">
              ¿Alguna vez arrancaste un mueble con toda la motivación… y a la
              mitad te trabaste?
            </p>
          </div>

          <div className="prose prose-lg mx-auto text-gray-700" data-id="element-16">
            <p className="mb-6" data-id="element-17">
              No porque no tengas ganas. Sino porque de repente aparecen todas
              las dudas juntas:
            </p>
            <ul className="space-y-3 mb-8 list-none pl-0" data-id="element-18">
              <li className="flex items-start gap-3 bg-stone-50 p-3 rounded-lg" data-id="element-19">
                <span className="text-red-500 font-bold" data-id="element-20">✕</span> "¿Cómo seguía
                esto?"
              </li>
              <li className="flex items-start gap-3 bg-stone-50 p-3 rounded-lg" data-id="element-21">
                <span className="text-red-500 font-bold" data-id="element-22">✕</span> "¿Esta medida
                está bien?"
              </li>
              <li className="flex items-start gap-3 bg-stone-50 p-3 rounded-lg" data-id="element-23">
                <span className="text-red-500 font-bold" data-id="element-24">✕</span> "¿En qué orden
                lo armo?"
              </li>
              <li className="flex items-start gap-3 bg-stone-50 p-3 rounded-lg" data-id="element-25">
                <span className="text-red-500 font-bold" data-id="element-26">✕</span> "¿Y si queda
                torcido?"
              </li>
              <li className="flex items-start gap-3 bg-stone-50 p-3 rounded-lg" data-id="element-27">
                <span className="text-red-500 font-bold" data-id="element-28">✕</span> "¿Y si me
                equivoco y tiro madera?"
              </li>
              <li className="flex items-start gap-3 bg-stone-50 p-3 rounded-lg" data-id="element-29">
                <span className="text-red-500 font-bold" data-id="element-30">✕</span> "¿Y cuánto
                cobro esto?"
              </li>
            </ul>

            <p className="mb-6 font-medium" data-id="element-31">
              Y ahí pasa lo que pasa siempre: volvés a improvisar.
            </p>
            <p className="mb-6" data-id="element-32">
              Y la improvisación en carpintería se paga caro: en madera, en
              tiempo, en retrabajo… y en confianza.
            </p>
            <p className="mb-8 font-bold text-xl text-stone-800" data-id="element-33">
              Porque hay algo peor que un corte mal hecho: la sensación de "otra
              vez me quedé a mitad".
            </p>

            <button onClick={scrollToPricing} className="w-full bg-orange-500 hover:bg-orange-600 text-white font-black text-xl py-4 px-8 rounded-xl shadow-lg transform transition hover:-translate-y-1 active:translate-y-0 flex items-center justify-center gap-2 mb-12" data-id="element-34">
              SÍ, QUIERO TRABAJAR CON MÉTODO <ArrowRight className="w-6 h-6" data-id="element-35" />
            </button>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 px-4 bg-stone-900 text-white" data-id="element-36">
        <div className="max-w-3xl mx-auto text-center" data-id="element-37">
          <h2 className="text-3xl md:text-4xl font-bold mb-8" data-id="element-38">
            La verdad es esta: <br data-id="element-39" />
            <span className="text-orange-500" data-id="element-40">
              No te falta talento. Te falta método.
            </span>
          </h2>

          <p className="text-xl text-stone-300 mb-8 leading-relaxed" data-id="element-41">
            Y si querés que tu carpintería sea algo más que un hobby caro,
            necesitás un sistema que te diga:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left mb-12" data-id="element-42">
            <div className="bg-stone-800 p-4 rounded-lg flex items-center gap-3 border border-stone-700" data-id="element-43">
              <Check className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-44" />
              <span data-id="element-45">Qué proyecto conviene hacer</span>
            </div>
            <div className="bg-stone-800 p-4 rounded-lg flex items-center gap-3 border border-stone-700" data-id="element-46">
              <Check className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-47" />
              <span data-id="element-48">Cómo hacerlo paso a paso sin trabarte</span>
            </div>
            <div className="bg-stone-800 p-4 rounded-lg flex items-center gap-3 border border-stone-700" data-id="element-49">
              <Check className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-50" />
              <span data-id="element-51">Cuánto cobrar sin miedo</span>
            </div>
            <div className="bg-stone-800 p-4 rounded-lg flex items-center gap-3 border border-stone-700" data-id="element-52">
              <Check className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-53" />
              <span data-id="element-54">Cómo mostrarlo para vender</span>
            </div>
          </div>
        </div>
      </section>

      {/* Product Intro */}
      <section className="py-16 px-4 bg-amber-50" data-id="element-55">
        <div className="max-w-4xl mx-auto" data-id="element-56">
          <div className="text-center mb-12" data-id="element-57">
            <h2 className="text-4xl font-black text-stone-900 mb-4" data-id="element-58">
              Esto es KIT CARPINTERÍA PRO
            </h2>
            <p className="text-xl text-stone-700 max-w-2xl mx-auto" data-id="element-59">
              KIT CARPINTERÍA PRO es un pack digital diseñado para que dejes de
              improvisar y empieces a construir proyectos vendibles con un
              método claro.
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-xl border border-amber-100 mb-12" data-id="element-60">
            <p className="text-lg text-center font-medium text-stone-800 mb-8" data-id="element-61">
              No es "otro ebook más" para leer y dejar guardado. Es un sistema
              práctico para que puedas:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center text-sm md:text-base font-bold text-stone-700" data-id="element-62">
              <div className="flex flex-col items-center gap-2" data-id="element-63">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center text-orange-600" data-id="element-64">
                  <Ruler className="w-6 h-6" data-id="element-65" />
                </div>
                Trabajar más prolijo
              </div>
              <div className="flex flex-col items-center gap-2" data-id="element-66">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center text-orange-600" data-id="element-67">
                  <Hammer className="w-6 h-6" data-id="element-68" />
                </div>
                Perder menos material
              </div>
              <div className="flex flex-col items-center gap-2" data-id="element-69">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center text-orange-600" data-id="element-70">
                  <Timer className="w-6 h-6" data-id="element-71" />
                </div>
                Avanzar más rápido
              </div>
              <div className="flex flex-col items-center gap-2" data-id="element-72">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center text-orange-600" data-id="element-73">
                  <Calculator className="w-6 h-6" data-id="element-74" />
                </div>
                Cobrar con criterio
              </div>
              <div className="flex flex-col items-center gap-2 col-span-2 md:col-span-1" data-id="element-75">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center text-orange-600" data-id="element-76">
                  <Instagram className="w-6 h-6" data-id="element-77" />
                </div>
                Vender con intención
              </div>
            </div>
          </div>

          {/* Features Detail */}
          <div className="space-y-12" data-id="element-78">
            <div className="flex flex-col md:flex-row gap-8 items-start" data-id="element-79">
              <div className="bg-stone-900 text-white p-4 rounded-xl shrink-0" data-id="element-80">
                <BookOpen className="w-8 h-8" data-id="element-81" />
              </div>
              <div data-id="element-82">
                <h3 className="text-2xl font-bold text-stone-900 mb-3" data-id="element-83">
                  ✅ Planos imprimibles paso a paso
                </h3>
                <p className="text-gray-700 mb-2" data-id="element-84">
                  Para que no dependas del "a ver cómo sale". Vas a tener una
                  guía clara para seguir sin perderte: qué medir, qué cortar,
                  qué revisar y en qué orden armar.
                </p>
                <p className="font-bold text-orange-600" data-id="element-85">
                  Menos errores. Menos retrabajo. Más prolijidad real.
                </p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-8 items-start" data-id="element-86">
              <div className="bg-stone-900 text-white p-4 rounded-xl shrink-0" data-id="element-87">
                <Hammer className="w-8 h-8" data-id="element-88" />
              </div>
              <div data-id="element-89">
                <h3 className="text-2xl font-bold text-stone-900 mb-3" data-id="element-90">
                  ✅ +60 proyectos listos en PDF
                </h3>
                <p className="text-gray-700 mb-2" data-id="element-91">
                  ¿Te bloqueás porque no sabés qué hacer? Listo. Acá tenés un
                  catálogo para elegir y producir sin arrancar de cero cada vez.
                </p>
                <p className="font-bold text-orange-600" data-id="element-92">
                  Proyectos ideales para avanzar, practicar y generar productos
                  vendibles.
                </p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-8 items-start" data-id="element-93">
              <div className="bg-stone-900 text-white p-4 rounded-xl shrink-0" data-id="element-94">
                <Calculator className="w-8 h-8" data-id="element-95" />
              </div>
              <div data-id="element-96">
                <h3 className="text-2xl font-bold text-stone-900 mb-3" data-id="element-97">
                  ✅ Calculadora automática de precios (.xlsx)
                </h3>
                <p className="text-gray-700 mb-2" data-id="element-98">
                  Este es el cambio que te saca el miedo a presupuestar. Porque
                  si cobrás "a ojo", casi siempre pasa una de dos: cobrás poco y
                  regalás tu trabajo o tirás un número alto y después dudás.
                </p>
                <p className="font-bold text-orange-600" data-id="element-99">
                  Con la calculadora, pasás a cobrar con criterio. Dejás de
                  adivinar. Empezás a presupuestar como oficio.
                </p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-8 items-start" data-id="element-100">
              <div className="bg-stone-900 text-white p-4 rounded-xl shrink-0" data-id="element-101">
                <Instagram className="w-8 h-8" data-id="element-102" />
              </div>
              <div data-id="element-103">
                <h3 className="text-2xl font-bold text-stone-900 mb-3" data-id="element-104">
                  ✅ Guías para vender y crecer en redes
                </h3>
                <p className="text-gray-700 mb-2" data-id="element-105">
                  Porque no alcanza con construir bien. Si no sabés mostrarlo,
                  nadie te compra. Acá tenés una guía práctica para: publicar
                  con intención, presentar tus trabajos mejor, generar consultas
                  reales y vender sin rogar ni perseguir.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-16 px-4 bg-white" data-id="element-106">
        <div className="max-w-3xl mx-auto" data-id="element-107">
          <h2 className="text-3xl font-bold text-center mb-10" data-id="element-108">
            ¿Para quién es?
          </h2>
          <div className="grid gap-4" data-id="element-109">
            <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-lg border-l-4 border-orange-500" data-id="element-110">
              <Check className="w-6 h-6 text-orange-500 shrink-0" data-id="element-111" />
              <p className="font-medium" data-id="element-112">
                Querés mejorar rápido con un camino claro, sin inventar todo
                desde cero
              </p>
            </div>
            <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-lg border-l-4 border-orange-500" data-id="element-113">
              <Check className="w-6 h-6 text-orange-500 shrink-0" data-id="element-114" />
              <p className="font-medium" data-id="element-115">
                Querés hacer proyectos que se puedan vender
              </p>
            </div>
            <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-lg border-l-4 border-orange-500" data-id="element-116">
              <Check className="w-6 h-6 text-orange-500 shrink-0" data-id="element-117" />
              <p className="font-medium" data-id="element-118">
                Ya sabés algo, pero te falta método, prolijidad y precio
                correcto
              </p>
            </div>
            <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-lg border-l-4 border-orange-500" data-id="element-119">
              <Check className="w-6 h-6 text-orange-500 shrink-0" data-id="element-120" />
              <p className="font-medium" data-id="element-121">
                Querés trabajar desde casa/taller y convertir tu habilidad en
                algo rentable
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why it works */}
      <section className="py-16 px-4 bg-stone-900 text-white" data-id="element-122">
        <div className="max-w-3xl mx-auto text-center" data-id="element-123">
          <h2 className="text-3xl font-bold mb-8" data-id="element-124">
            ¿Por qué esto funciona cuando lo demás no?
          </h2>
          <p className="text-lg text-stone-300 mb-8" data-id="element-125">
            Porque lo que hay afuera son pedazos sueltos: videos sueltos, ideas
            sueltas, planos sueltos, precios que te dicen otros sin criterio.
          </p>
          <p className="text-xl font-bold text-white mb-10" data-id="element-126">
            Y vos no necesitás más información. Necesitás estructura.
          </p>

          <div className="bg-stone-800 p-8 rounded-2xl border border-stone-700 mb-10" data-id="element-127">
            <h3 className="text-2xl font-bold text-orange-500 mb-4" data-id="element-128">
              KIT CARPINTERÍA PRO te da el sistema completo:
            </h3>
            <div className="flex flex-wrap justify-center items-center gap-4 text-lg font-bold" data-id="element-129">
              <span data-id="element-130">Elegís</span>
              <ArrowRight className="text-stone-500" data-id="element-131" />
              <span data-id="element-132">Construís</span>
              <ArrowRight className="text-stone-500" data-id="element-133" />
              <span data-id="element-134">Cobrás</span>
              <ArrowRight className="text-stone-500" data-id="element-135" />
              <span data-id="element-136">Vendés</span>
            </div>
            <p className="mt-4 text-stone-400" data-id="element-137">
              Sin improvisar. Sin perder. Sin regalarte.
            </p>
          </div>
        </div>
      </section>

      {/* Bonuses */}
      <section className="py-16 px-4 bg-amber-50" data-id="element-138">
        <div className="max-w-5xl mx-auto" data-id="element-139">
          <h2 className="text-4xl font-black text-center text-stone-900 mb-12 uppercase tracking-tight" data-id="element-140">
            Bonos Gratis Por Hoy
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-141">
            <img src="https://cdn.magicpatterns.com/uploads/evAb4j4LGUjG3tbBTVKipB/Calculadora_para_carpinteros_en_Excel.png" alt="Bono Calculadora" className="w-full h-auto rounded-xl shadow-lg cursor-pointer hover:scale-[1.02] transition-transform" onClick={scrollToPricing} data-id="element-142" />
            <img src="https://cdn.magicpatterns.com/uploads/p3fsLFKBStT8iLGQxs8yEL/Disenos_de_muebles_madera_para_espacios_pequenos.png" alt="Bono Diseños de Muebles" className="w-full h-auto rounded-xl shadow-lg cursor-pointer hover:scale-[1.02] transition-transform" onClick={scrollToPricing} data-id="element-143" />
            <img src="https://cdn.magicpatterns.com/uploads/uyUgKP6z2bTksUWDpRywSM/Manual_de_reparacion_y_restauracion_de_madera.png" alt="Bono Manual Reparación" className="w-full h-auto rounded-xl shadow-lg cursor-pointer hover:scale-[1.02] transition-transform" onClick={scrollToPricing} data-id="element-144" />
            <img src="https://cdn.magicpatterns.com/uploads/hanBLo2NLXEtyzRC9VydWU/Seguridad_y_prevencion_en_tu_taller.png" alt="Bono Seguridad" className="w-full h-auto rounded-xl shadow-lg cursor-pointer hover:scale-[1.02] transition-transform" onClick={scrollToPricing} data-id="element-145" />
            <img src="https://cdn.magicpatterns.com/uploads/5QtsZVEsGiZoQcY2HHF7dg/Guia_para_carpinteros_en_redes.png" alt="Bono Redes" className="w-full h-auto rounded-xl shadow-lg cursor-pointer hover:scale-[1.02] transition-transform" onClick={scrollToPricing} data-id="element-146" />
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <div id="pricing" className="bg-stone-900 pb-16" data-id="element-147">
        <CountdownTimer data-id="element-148" />

        <div className="max-w-6xl mx-auto px-4 py-16" data-id="element-149">
          <h2 className="text-3xl md:text-4xl font-black text-center text-white mb-12" data-id="element-150">
            ELEGÍ TU PACK Y EMPEZÁ HOY
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end" data-id="element-151">
            {/* Tier 1 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-stone-200 flex flex-col h-full" data-id="element-152">
              <div className="p-6 bg-stone-100 border-b border-stone-200" data-id="element-153">
                <h3 className="text-xl font-black text-stone-800 text-center" data-id="element-154">
                  KIT CARPINTERÍA PRO
                </h3>
                <p className="text-center text-stone-500 text-sm mt-1" data-id="element-155">
                  El sistema esencial
                </p>
              </div>
              <div className="p-6 flex-grow flex flex-col" data-id="element-156">
                <div className="text-center mb-6" data-id="element-157">
                  <span className="text-stone-400 line-through text-lg block" data-id="element-158">
                    $49,990
                  </span>
                  <span className="text-4xl font-black text-stone-900" data-id="element-159">
                    $19,990
                  </span>
                </div>
                <ul className="space-y-3 mb-8 flex-grow" data-id="element-160">
                  <li className="flex items-start gap-2 text-sm" data-id="element-161">
                    <Check className="w-5 h-5 text-green-500 shrink-0" data-id="element-162" />
                    <span data-id="element-163">Kit Carpintero Pro Completo</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm" data-id="element-164">
                    <Check className="w-5 h-5 text-green-500 shrink-0" data-id="element-165" />
                    <span data-id="element-166">5 Bonos Exclusivos</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm" data-id="element-167">
                    <Check className="w-5 h-5 text-green-500 shrink-0" data-id="element-168" />
                    <span data-id="element-169">Acceso de por vida</span>
                  </li>
                </ul>
                <a href="https://checkout.solucionstore.shop/cart/4510017292591:1" className="w-full bg-stone-800 hover:bg-stone-700 text-white font-bold py-3 px-4 rounded-lg text-center transition-colors text-sm md:text-base" data-id="element-170">
                  QUIERO EL KIT CARPINTERÍA PRO
                </a>
              </div>
            </div>

            {/* Tier 2 - Recommended */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-2xl border-2 border-orange-500 transform md:-translate-y-4 relative z-10 flex flex-col h-full" data-id="element-171">
              <div className="bg-orange-500 text-white text-center py-2 font-bold text-sm tracking-widest uppercase" data-id="element-172">
                MÁS POPULAR
              </div>
              <div className="p-6 bg-orange-50 border-b border-orange-100" data-id="element-173">
                <h3 className="text-2xl font-black text-stone-900 text-center" data-id="element-174">
                  PACK CONSTRUCTOR
                </h3>
                <p className="text-center text-stone-600 text-sm mt-1" data-id="element-175">
                  Para ampliar tus servicios
                </p>
              </div>
              <div className="p-6 flex-grow flex flex-col" data-id="element-176">
                <div className="text-center mb-6" data-id="element-177">
                  <span className="text-stone-400 line-through text-lg block" data-id="element-178">
                    $59,900
                  </span>
                  <span className="text-5xl font-black text-orange-600" data-id="element-179">
                    $27,990
                  </span>
                </div>
                <ul className="space-y-3 mb-8 flex-grow" data-id="element-180">
                  <li className="flex items-start gap-2 font-medium" data-id="element-181">
                    <Check className="w-5 h-5 text-green-500 shrink-0" data-id="element-182" />
                    <span data-id="element-183">Todo lo del Kit Carpintero Pro</span>
                  </li>
                  <li className="flex items-start gap-2 font-medium" data-id="element-184">
                    <Check className="w-5 h-5 text-green-500 shrink-0" data-id="element-185" />
                    <span data-id="element-186">+ Curso de Electricidad</span>
                  </li>
                  <li className="flex items-start gap-2 font-medium" data-id="element-187">
                    <Check className="w-5 h-5 text-green-500 shrink-0" data-id="element-188" />
                    <span data-id="element-189">+ Curso de Albañilería</span>
                  </li>
                  <li className="flex items-start gap-2 font-medium" data-id="element-190">
                    <Check className="w-5 h-5 text-green-500 shrink-0" data-id="element-191" />
                    <span data-id="element-192">5 Bonos Exclusivos</span>
                  </li>
                </ul>
                <a href="https://checkout.solucionstore.shop/cart/45100208324799:1" className="w-full bg-orange-500 hover:bg-orange-600 text-white font-black py-4 px-4 rounded-lg text-center shadow-lg transition-transform hover:scale-[1.02]" data-id="element-193">
                  QUIERO EL PACK CONSTRUCTOR
                </a>
              </div>
            </div>

            {/* Tier 3 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-stone-200 flex flex-col h-full" data-id="element-194">
              <div className="bg-stone-800 text-white text-center py-2 font-bold text-sm tracking-widest uppercase" data-id="element-195">
                MEJOR VALOR
              </div>
              <div className="p-6 bg-stone-100 border-b border-stone-200" data-id="element-196">
                <h3 className="text-xl font-black text-stone-800 text-center" data-id="element-197">
                  PACK MAESTRO
                </h3>
                <p className="text-center text-stone-500 text-sm mt-1" data-id="element-198">
                  La formación completa
                </p>
              </div>
              <div className="p-6 flex-grow flex flex-col" data-id="element-199">
                <div className="text-center mb-6" data-id="element-200">
                  <span className="text-stone-400 line-through text-lg block" data-id="element-201">
                    $69,900
                  </span>
                  <span className="text-4xl font-black text-stone-900" data-id="element-202">
                    $39,990
                  </span>
                </div>
                <ul className="space-y-3 mb-8 flex-grow" data-id="element-203">
                  <li className="flex items-start gap-2 text-sm" data-id="element-204">
                    <Check className="w-5 h-5 text-green-500 shrink-0" data-id="element-205" />
                    <span data-id="element-206">Todo lo del Pack Constructor</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm" data-id="element-207">
                    <Check className="w-5 h-5 text-green-500 shrink-0" data-id="element-208" />
                    <span data-id="element-209">+ Curso de Pintura Profesional</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm" data-id="element-210">
                    <Check className="w-5 h-5 text-green-500 shrink-0" data-id="element-211" />
                    <span data-id="element-212">Biblioteca completa de oficios</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm" data-id="element-213">
                    <Check className="w-5 h-5 text-green-500 shrink-0" data-id="element-214" />
                    <span data-id="element-215">Todos los Bonos incluidos</span>
                  </li>
                </ul>
                <a href="https://checkout.solucionstore.shop/cart/45100212519103:1" className="w-full bg-stone-800 hover:bg-stone-700 text-white font-bold py-3 px-4 rounded-lg text-center transition-colors text-sm md:text-base" data-id="element-216">
                  QUIERO EL PACK COMPLETO
                </a>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center text-stone-400 text-sm flex flex-col md:flex-row justify-center items-center gap-4" data-id="element-217">
            <span className="flex items-center gap-2" data-id="element-218">
              <ShieldCheck className="w-4 h-4" data-id="element-219" /> Pago 100% Seguro
            </span>
            <span className="hidden md:inline" data-id="element-220">•</span>
            <span className="flex items-center gap-2" data-id="element-221">
              <Check className="w-4 h-4" data-id="element-222" /> Entrega Inmediata por Email
            </span>
            <span className="hidden md:inline" data-id="element-223">•</span>
            <span className="flex items-center gap-2" data-id="element-224">
              <Check className="w-4 h-4" data-id="element-225" /> Acceso de por vida
            </span>
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <section className="py-16 px-4 bg-white" data-id="element-226">
        <div className="max-w-6xl mx-auto" data-id="element-227">
          <h2 className="text-3xl font-bold text-center mb-12" data-id="element-228">
            Lo que dicen quienes ya trabajan con método
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-229">
            {testimonials.map((t, i) => <TestimonialCard key={i} {...t} data-id="element-230" />)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-4 bg-stone-100 border-t border-stone-200" data-id="element-231">
        <div className="max-w-3xl mx-auto text-center" data-id="element-232">
          <h2 className="text-3xl font-bold mb-6" data-id="element-233">P.D.</h2>
          <p className="text-lg text-stone-700 mb-4" data-id="element-234">
            La improvisación te cobra una cuota: en madera, en tiempo y en
            frustración.
          </p>
          <p className="text-lg text-stone-700 mb-8" data-id="element-235">
            Podés seguir juntando ideas… o podés empezar a trabajar como oficio,
            con un sistema claro.
          </p>

          <button onClick={scrollToPricing} className="w-full md:w-auto bg-orange-500 hover:bg-orange-600 text-white font-black text-xl py-4 px-12 rounded-xl shadow-lg transform transition hover:-translate-y-1 active:translate-y-0" data-id="element-236">
            SÍ, QUIERO TRABAJAR CON MÉTODO
          </button>
        </div>
      </section>

      <footer className="bg-stone-900 text-stone-500 py-8 text-center text-sm" data-id="element-237">
        <p data-id="element-238">© 2024 KIT CARPINTERÍA PRO. Todos los derechos reservados.</p>
      </footer>
    </div>;
}